import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FeatureFlags {
  adsEnabled: boolean;
  premiumFeatures: boolean;
  feedbackPrompts: boolean;
}

interface UseFeatureFlagsReturn {
  flags: FeatureFlags;
  loading: boolean;
  isEnabled: (flagName: keyof FeatureFlags) => boolean;
  refresh: () => Promise<void>;
}

const DEFAULT_FLAGS: FeatureFlags = {
  adsEnabled: false,
  premiumFeatures: true,
  feedbackPrompts: true,
};

// Map database flag names to our typed keys
const FLAG_NAME_MAP: Record<string, keyof FeatureFlags> = {
  ads_enabled: "adsEnabled",
  premium_features: "premiumFeatures",
  feedback_prompts: "feedbackPrompts",
};

export const useFeatureFlags = (): UseFeatureFlagsReturn => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("flag_name, enabled");

      if (error) {
        console.error("Failed to load feature flags");
        return;
      }

      if (data) {
        const newFlags = { ...DEFAULT_FLAGS };
        data.forEach((flag) => {
          const mappedKey = FLAG_NAME_MAP[flag.flag_name];
          if (mappedKey) {
            newFlags[mappedKey] = flag.enabled;
          }
        });
        setFlags(newFlags);
      }
    } catch (err) {
      console.error("Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const isEnabled = useCallback(
    (flagName: keyof FeatureFlags): boolean => {
      return flags[flagName];
    },
    [flags]
  );

  return {
    flags,
    loading,
    isEnabled,
    refresh: fetchFlags,
  };
};
